import { ChangeDetectionStrategy, Component, inject, Input, SimpleChanges } from '@angular/core';
import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { Status } from '@df/core/status';
import { NgInject } from '@df/ng/ng-inject';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { InlineButtonComponent } from 'projects/app-default/src/app/ui/buttons/inline-button/inline-button.component';
import { NProductViewSurchargeModal } from '../modal/product-view-surcharge-modal.component';

export namespace NProductViewSurchargeNote {
    export const BLOCK_ID = 'product-surcharge-note';

    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    export interface IBlockData extends Catalog.IBlockData<IBlockDataContent> {}

    export interface IBlockDataContent {
        copy: string;
        ctaCopy: string;
    }

    export enum ELayout {
        bundle,
        default
    }
}

@Component({
    selector: 'product-view-surcharge-note',
    templateUrl: './product-view-surcharge-note.html',
    styleUrls: ['./product-view-surcharge-note.scss'],
    standalone: true,
    imports: [ICmsClientModule, InlineButtonComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<ProductViewSurchargeNoteComponent>({
    id: NProductViewSurchargeNote.BLOCK_ID,
    block: {
        url: NProductViewSurchargeNote.BLOCK_ID
    },
    name: 'Surcharge information',
    description: 'Global block information about delivery surcharge',
    onDemandOnly: true,
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-general',
            title: 'General'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'copy',
            title: 'Copy',
            default: 'This product may be subject<br/>to delivery surcharges.',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'ctaCopy',
            title: 'CTA Copy',
            default: 'More info',
            required: true
        }
    ]
})
export class ProductViewSurchargeNoteComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @Input({
        required: true
    })
    product!: Product<IProductDataV3>;

    @Input({
        required: true
    })
    variant!: 'product' | 'basket';

    @ICmsContent({
        type: 'blockContent'
    })
    content!: NProductViewSurchargeNote.IBlockDataContent;

    protected modalStatus = new Status();

    private dfModalService = inject(DfModalService);

    private dfApiConfigService = inject(DfApiConfigService);

    override ngOnInit(): void {
        this.subscriptions.add(
            (this as NICmsContentComponent.IComponent).iCmsValidation$!.subscribe(event => {
                if (event) {
                    if (!this.product?.data) {
                        event.error = {
                            level: 'info',
                            copy: 'Missing context product.'
                        };
                    } else if (!this.product.data.surcharge) {
                        event.error = {
                            level: 'info',
                            copy: "Current product doesn't have a delivery surcharge."
                        };
                    }
                }
            })
        );
        super.ngOnInit();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        // force revalidation on product change
        if (this.initiated && changes['product']) {
            (this as NICmsContentComponent.IComponent).iCmsValidation$?.next();
        }
        super.ngOnChanges(changes);
    }

    protected openModal() {
        this._openModal();
    }

    private async _openModal() {
        const data: NProductViewSurchargeModal.IModalData = {
            surcharge: this.product.data!.surcharge!
        };
        this.modalStatus.busy = true;
        await this.dfApiConfigService.loadCountries();
        this.dfModalService.open(this.modalStatus, NProductViewSurchargeModal.BLOCK_ID, undefined, data);
    }
}
